// src/pages/RegularUserProfile.js

import React, { useEffect } from "react";
import useUrlTab from "../../../hooks/useUrlTab";
import PlaylistTile from "../../../components/music/PlaylistTile";
import OfflinePlaybackCard from "../../../components/profile/OfflinePlaybackCard";

// Library Icons
import libraryIcon from "../../../assets/library.png";
import libraryDark from "../../../assets/library_darkmode.png";
import libraryActive from "../../../assets/library_active.png";

// Playlists Icons
import playlistsIcon from "../../../assets/playlists.png";
import playlistsDark from "../../../assets/playlists_darkmode.png";
import playlistsActive from "../../../assets/playlists_active.png";

import { useProfile } from "../../../context/ProfileContext";

const RegularUserProfile = ({
  userProfile,
  playlists = [],
  onPlaylistClick,
  isPublicView = false,
}) => {
  const [activeTab, handleTabChange] = useUrlTab("library");
  const { isDarkMode } = useProfile();

  const getTabIcon = (id) => {
    if (id === "library") {
      if (activeTab === "library") return libraryActive;
      return isDarkMode ? libraryDark : libraryIcon;
    }
    if (id === "playlists") {
      if (activeTab === "playlists") return playlistsActive;
      return isDarkMode ? playlistsDark : playlistsIcon;
    }
  };

  const tabs = [
    ...(!isPublicView ? [{ id: "library", label: "Library" }] : []),
    { id: "playlists", label: "Playlists" },
  ];

  useEffect(() => {
    if (isPublicView && activeTab === "library") {
      handleTabChange("playlists");
    }
  }, [isPublicView, activeTab, handleTabChange]);

  return (
    <div className="flex flex-col items-center mt-2 transition-colors">
      {/* Tab Navigation Icons */}
      <div className="flex justify-center mt-8 space-x-12">
        {tabs.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => handleTabChange(id)}
            className="flex flex-col items-center group transition-transform active:scale-95"
          >
            <div className="w-14 h-14 flex items-center justify-center">
              <img
                src={getTabIcon(id)}
                alt={label}
                className="w-10 h-10 object-contain"
              />
            </div>
            <p
              className={`text-[10px] mt-1 font-black uppercase tracking-widest transition-colors ${
                activeTab === id
                  ? "text-[#ff00cc]"
                  : "text-gray-500 dark:text-brand-gray"
              }`}
            >
              {label}
            </p>
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="mt-8 px-4 w-full max-w-screen-md">
        {/* 🌟 1. LIBRARY TAB: Displays the single Spotify Home-style card */}
        {activeTab === "library" && (
          <div className="w-full">
            {/* Spotify-style 2-column card grid */}
            <div className="grid grid-cols-2 gap-2.5 sm:gap-4 w-full">
              <OfflinePlaybackCard />
              {/* Any future pinned library cards or saved collection items will occupy the second slot here */}
            </div>
          </div>
        )}
        {/* 2. PLAYLISTS TAB: Displays user-created playlists */}
        {activeTab === "playlists" && (
          <>
            {playlists.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-brand-gray py-12 italic">
                No playlists found. Create one by adding a song to a new playlist!
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                {playlists.map((playlist) => (
                  <PlaylistTile
                    key={playlist.id}
                    playlist={playlist}
                    onClick={() => onPlaylistClick(playlist)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RegularUserProfile;